/**
 * Sistema de eventos para comunicación entre AuthContext y componentes de formulario
 * sobre el estado de la sesión y expiración inminente
 */

// Constantes de eventos
export const SESSION_EXPIRING_SOON = 'session:expiring-soon';
export const SESSION_EXPIRING = 'session:expiring';
export const SESSION_EXTENDED = 'session:extended';

// Tipos para los detalles de eventos
export interface SessionExpiringSoonDetail {
  timeRemaining: number; // Tiempo restante en milisegundos
  expiresAt: number; // Timestamp de expiración
}

export interface SessionExpiringDetail {
  reason: 'expired' | 'inactive';
  timeRemaining: number; // Tiempo restante en milisegundos para guardado de emergencia
}

export interface SessionExtendedDetail {
  newExpiresAt: number; // Nuevo timestamp de expiración
}

// Declarar eventos globales
declare global {
  interface WindowEventMap {
    [SESSION_EXPIRING_SOON]: CustomEvent<SessionExpiringSoonDetail>;
    [SESSION_EXPIRING]: CustomEvent<SessionExpiringDetail>;
    [SESSION_EXTENDED]: CustomEvent<SessionExtendedDetail>;
  }
}

/**
 * Emite evento de advertencia de expiración próxima
 */
export const emitSessionExpiringSoon = (timeRemaining: number, expiresAt: number): void => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  const event = new CustomEvent<SessionExpiringSoonDetail>(SESSION_EXPIRING_SOON, {
    detail: {
      timeRemaining,
      expiresAt,
    },
  });

  window.dispatchEvent(event);
};

/**
 * Emite evento de expiración inminente (guardado de emergencia)
 */
export const emitSessionExpiring = (reason: 'expired' | 'inactive', timeRemaining: number): void => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  const event = new CustomEvent<SessionExpiringDetail>(SESSION_EXPIRING, {
    detail: {
      reason,
      timeRemaining,
    },
  });

  window.dispatchEvent(event);
};

/**
 * Emite evento de sesión extendida
 */
export const emitSessionExtended = (newExpiresAt: number): void => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  const event = new CustomEvent<SessionExtendedDetail>(SESSION_EXTENDED, {
    detail: {
      newExpiresAt,
    },
  });

  window.dispatchEvent(event);
};

/**
 * Helper para escuchar eventos de sesión con tipado seguro
 */
export const addSessionEventListener = <T extends keyof WindowEventMap>(
  eventName: T,
  handler: (event: WindowEventMap[T]) => void
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  window.addEventListener(eventName, handler as EventListener);

  return () => {
    window.removeEventListener(eventName, handler as EventListener);
  };
};

